export enum EProcessingStatus {
  Pending = 'pending',
  PendingBatch = 'pendingbatch',
  Stored = 'stored',
  Processing = 'processing',
  Classified = 'classified',
  Extracted = 'extracted',
  Processed = 'processed',
  Matched = 'matched',
  PartiallySent = 'partiallysent',
  Finalized = 'finalized',
  Discarded = 'discarded',
  Failed = 'failed',
}

// IFP Status
export enum EIFPStatus {
  InCreation = 0,
  CreditInReview = 1,
  CreditApproved = 2,
  CreditRefused = 3,
  Conformity = 4,
  ReadyForPaidOut = 5,
  PaidOut = 6,
  Canceled = 7,
}

// FA Status
export enum EFAStatus {
  EnCreation = 0,
  EnAttenteDeDocuments = 1,
  Annulée = 2,
  Deboursée = 3,
}

// Product Lines
export enum EProductLine {
  FA = 0,
  IFP = 1,
  IFX = 2,
}

export const getStatusColor = (status: EProcessingStatus) => {
  switch (status) {
    case EProcessingStatus.Processed:
      return 'info'
    case EProcessingStatus.Matched:
      return 'success'
    case EProcessingStatus.Failed:
      return 'error'
    case EProcessingStatus.PartiallySent:
      return 'warning'
    case EProcessingStatus.Discarded:
      return 'warning'
    default:
      return 'default'
  }
}

export interface DocsAutomationFiltersType {
  clientNameContains: string | null
  email: string | null
  tagSearch: string | null
  status: EProcessingStatus[] | null
  tags: string[] | null
  limit: number
  offset: number
  sortField: string | null
  sortDirection: string | null
}

export interface CreditAppMatchResult {
  id: number
  dccNumber: number
  cvtNumber?: number
  productLine: number
  formattedProductLine: string
  office: string
  statusId: number
  status: string
  statusEn: string
  link: string
  creationDT: string // DateTime string
  error: string
  changeTrackingInfo?: boolean
}

export interface ExtractedData {
  contract: Record<string, unknown> | null
  drivingLicense: Record<string, unknown> | null
  paysub: Record<string, unknown> | null
  voidCheck: Record<string, unknown> | null
}

export interface Email {
  subject: string | null
  message: string | null
  receivedOn: string // DateTime string
  senderEmail: string
  sentBy: string
  searchableSentBy: string
  webLink: string
}

export interface ProcessingNote {
  createdByUserFullName: string
  createdOn: string // DateTime string
  note: string
}

export enum EDocumentWarningCode {
  PasswordProtected = 'PASSWORD_PROTECTED',
  UnsupportedFileType = 'UNSUPPORTED_FILE_TYPE',
  ProcessingError = 'PROCESSING_ERROR',
  AllAttachmentsUnsupported = 'ALL_ATTACHMENTS_UNSUPPORTED',
  SomeAttachmentsFailed = 'SOME_ATTACHMENTS_FAILED',
  AllAttachmentsFailed = 'ALL_ATTACHMENTS_FAILED',
  MalwareDetected = 'MALWARE_DETECTED',
  AllAttachmentsMalware = 'ALL_ATTACHMENTS_MALWARE',
}

export interface DocumentWarning {
  code: string
  fileName: string | null
}

export interface DocsAutomation {
  id: string
  containsContract: boolean
  email: Email | null
  extractedData: ExtractedData | null
  extractedReferenceNumbers: string[]
  systemMatchingResult: CreditAppMatchResult[] | null
  processingStatus: EProcessingStatus
  processingNotes: ProcessingNote[]
  warnings: DocumentWarning[]
  linkedCreditApp: CreditAppMatchResult | null
  originSystem: string
  sentDocumentTypeIds: number[]
  tags: string[]
  versionTag: string
  createdOn: string // DateTime string
  createdByUserFullname: string
  createdByUserId: string
  updatedOn: string // DateTime string
  updatedByUserFullname: string
  updatedByUserId: string
}

export interface LinkDocumentToCreditAppParams {
  documentId: string
  versionTag: string
  creditApplicationId?: number
  dccNumber?: number
  cvtNumber?: number
  financingProgram?: number
}

export interface SetDiscardStatusParams {
  documentId: string
  versionTag: string
  isDiscarded: boolean
  discardReason?: string
}

export interface FinalizeDocumentParams {
  documentId: string
  versionTag: string
  excludedDocumentTypeIds?: number[]
}

export interface UpdateDocumentTagsParams {
  documentId: string
  versionTag: string
  tags: string[]
}

export interface UpdateExtractedDataParams {
  documentId: string
  versionTag: string
  extractedData: Record<string, unknown>
}

export interface AssignDocumentsParams {
  count: number
}

export interface UploadedDocument {
  documentId: string | null
  fileName: string
  fileSize: string
  description: string
  documentTypeId: number | null
  documentTypeNameFr: string
  documentTypeNameEn: string
  statusId: number | null
  statusName: string
  isPrivate: boolean
  creationDT: string | null
  creationUserFullName: string
  statusChangedDT: string | null
  statusChangedByUserFullName: string
}

export enum EUploadedDocumentStatus {
  EnAttente = 0,
  EnApprobation = 1,
  Approuve = 2,
  Refuse = 3,
  Supprime = 4,
}

// Helper functions for finalization validation
export const isProductLineSupported = (productLine?: number): boolean => {
  return productLine === EProductLine.FA || productLine === EProductLine.IFP
}

export const isCreditAppStatusBlocked = (productLine?: number, statusId?: number): boolean => {
  if (productLine === EProductLine.IFP) {
    return statusId === EIFPStatus.PaidOut || statusId === EIFPStatus.Canceled
  }
  if (productLine === EProductLine.FA) {
    return statusId === EFAStatus.Annulée || statusId === EFAStatus.Deboursée
  }
  return false
}

export const getFinalizationBlockMessage = (link: CreditAppMatchResult | null): string | null => {
  if (!link) {
    return 'docsAutomation.mustLinkForFinalization'
  }

  if (!isProductLineSupported(link.productLine)) {
    return 'docsAutomation.programNotSupported'
  }

  return null
}
